/**
 * LINE OA Channel Management API Client (Phase 2)
 * Handles per-business LINE Official Account channel registration
 */

import axios, { getBusinessId } from '@/lib/api/axios-instance';

const BASE_URL = '/api/v2/business/line-channels';
const LINKING_URL = '/api/linking/customer/accounts';

// ============================================================================
// Types
// ============================================================================

export interface LineChannel {
  id: string;
  customer_id: string;
  business_id: string;
  branch_id: string | null;
  branch_name?: string | null;
  channel_id: string;
  channel_name: string | null;
  is_active: boolean;
  webhook_url?: string;
  created_at: string;
  updated_at: string;
}

export interface LinkedMessagingAccount {
  platform: string;
  platform_user_id: string;
  platform_username?: string;
  platform_data?: Record<string, unknown>;
  linked_at: string;
  last_activity_at: string | null;
  is_active: boolean;
  channel_source: 'shared' | 'custom' | 'group';
}

export interface LineChannelListResponse {
  success: boolean;
  data: LineChannel[];
}

export interface LineChannelResponse {
  success: boolean;
  data: LineChannel;
}

export interface RegisterChannelRequest {
  business_id: string;
  channel_id: string;
  channel_secret: string;
  channel_access_token: string;
  channel_name?: string;
  branch_id?: string | null;
}

export interface TestChannelResponse {
  success: boolean;
  data: {
    valid: boolean;
    botInfo?: {
      userId: string;
      basicId: string;
      displayName: string;
      pictureUrl?: string;
    };
    error?: string;
  };
}

// ============================================================================
// API Functions
// ============================================================================

export const lineChannelsAPI = {
  /**
   * List all LINE OA channels for the current business
   */
  async getAll(businessId?: string): Promise<LineChannel[]> {
    const bid = businessId || getBusinessId();
    const params = bid ? { business_id: bid } : {};
    const response = await axios.get<LineChannelListResponse>(BASE_URL, { params });
    return response.data.data;
  },

  /**
   * Register a new LINE OA channel for a business
   */
  async register(data: RegisterChannelRequest): Promise<LineChannel> {
    const businessId = data.business_id || getBusinessId();
    if (!businessId) {
      throw new Error('Business ID not found. Please login again.');
    }
    const response = await axios.post<LineChannelResponse>(BASE_URL, {
      ...data,
      business_id: businessId,
    });
    return response.data.data;
  },

  /**
   * Delete (disconnect) a LINE OA channel
   */
  async delete(channelId: string): Promise<void> {
    await axios.delete(`${BASE_URL}/${channelId}`);
  },

  /**
   * Test LINE OA channel credentials (calls LINE Bot Info API)
   */
  async test(channelId: string): Promise<TestChannelResponse['data']> {
    const response = await axios.post<TestChannelResponse>(`${BASE_URL}/${channelId}/test`);
    return response.data.data;
  },
};

// ============================================================================
// Linked Messaging Accounts API
// ============================================================================

export const linkedAccountsAPI = {
  /**
   * Get all messaging accounts linked to the current customer
   * Includes channel_source: 'shared' | 'custom' | 'group'
   */
  async getAll(): Promise<LinkedMessagingAccount[]> {
    const response = await axios.get<{ success: boolean; data: { accounts: LinkedMessagingAccount[]; totalCount: number } }>(LINKING_URL);
    console.log('[linkedAccountsAPI.getAll] raw response.data:', JSON.stringify(response.data));
    const accounts = response.data?.data?.accounts ?? [];
    console.log('[linkedAccountsAPI.getAll] accounts:', accounts);
    return accounts;
  },

  /**
   * Unlink a messaging account
   */
  async unlink(platform: string, platformUserId: string): Promise<void> {
    await axios.delete(`${LINKING_URL}/${platform}/${platformUserId}`);
  },

  /**
   * Check if customer has an active shared LINE OA link (LINE THAISLIPCHECK)
   */
  async isSharedLineLinked(): Promise<boolean> {
    try {
      const accounts = await linkedAccountsAPI.getAll();
      const matched = accounts.some(
        (a) => (a.platform === 'line' || a.platform === 'line-oa') && a.is_active && a.channel_source === 'shared'
      );
      console.log('[isSharedLineLinked] accounts:', accounts, '=> matched:', matched);
      return matched;
    } catch (err) {
      console.error('[isSharedLineLinked] ERROR:', err);
      return false;
    }
  },
};
